
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { PERMISSION } from '@/permissions';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import { formatOptionGroup, formatItem } from '../../utils/format';
import { OPTION } from '../../utils/constants';
import { valueSort } from '../../utils/sort';
import { formatValue } from '@/utils/format';
import { isFalseyButNotZero } from '@/utils/general';
import { scrollToFirstError } from '@/utils/errorHandling';
import { DateTime } from 'luxon';
import { isEqual } from 'lodash';
import axios from 'axios';
import Toast from '@/utils/toast';
import Pricing from '@/components/item/Pricing.vue';
import FileUpload from '@/components/shared/form/FileUpload.vue';
import Tooltip from '@/components/shared/Tooltip.vue';
import PriceScheduling from '@/components/shared/price-scheduling/PriceScheduling.vue';
import AddOnOptionGroupEditor from '@/components/add-ons/AddOnOptionGroupEditor.vue';
import ModalWrapper from '@/components/shared/ModalWrapper.vue';
import NutritionFacts from './NutritionFacts.vue';
import TaxOverwrite from './TaxOverwrite.vue';
import SelectableAddOnOptionGroupsList from './SelectableAddOnOptionGroupsList.vue';
import ItemLocaleEditor from './ItemLocaleEditor.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import SlideFadeTransition from '@/components/shared/transition/SlideFadeTransition.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextField from '@/components/shared/TextField.vue';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import SelectComponent from '@/components/shared/Select.vue';
import '@/validation-rules';

const namespace: string = 'items';

@Component<ItemEditor>({
	components: {
		Pricing,
		FileUpload,
		Tooltip,
		PriceScheduling,
		AddOnOptionGroupEditor,
		ModalWrapper,
		NutritionFacts,
		TaxOverwrite,
		SelectableAddOnOptionGroupsList,
		ItemLocaleEditor,
		StandardModal,
		SlideFadeTransition,
		InfoBanner,
		ValidationObserver,
		ValidationProvider,
		TextField,
		ToggleSwitch,
		SelectComponent
	}
})
export default class ItemEditor extends Vue {
	// Options
	@Action('fetchAddOnOptionGroups', { namespace: 'options' }) private fetchAddOnOptionGroups!: () => Promise<void>;
	@Action('fetchOptionGroupsByItem', { namespace: 'options' }) private fetchOptionGroupsByItem!: (payload: object) => Promise<void>;
	@Action('clearItemOptionGroups', { namespace: 'options' }) private clearItemOptionGroups!: () => void;
	@Action('createOptionGroup', { namespace: 'options' }) private createPricingOptionGroupAction!: (args: object) => Promise<number | undefined>;
	@Action('setSelectedAddOnOptionGroup', { namespace: 'options' }) private setSelectedAddOnOptionGroup!: (item: OptionGroup | object) => Promise<void>;
	@Action('updateOptionGroupAndOptions', { namespace: 'options' }) private updateOptionGroupAndOptions!: (args: object) => Promise<void>;
	@Action('createItemOptionGroupAssociation', { namespace: 'options' }) private createItemOptionGroupAssociation!: (args: object) => Promise<void>;
	@Action('deleteItemOptionGroupAssociation', { namespace: 'options' }) private deleteItemOptionGroupAssociation!: (args: object) => Promise<void>;
	@Getter('getAddOnOptionGroups', { namespace: 'options' }) private allAddOnOptionGroups!: OptionGroup[];
	@Getter('getItemOptionGroups', { namespace: 'options' }) private itemOptionGroups!: OptionGroup[];

	// Items
	@Action('updateMenuItem', { namespace }) private updateMenuItem!: (payload: object) => Promise<void>;
	@Action('createMenuItem', { namespace }) private createMenuItem!: (payload: object) => Promise<void>;
	@Action('updateMenuSectionItem', { namespace }) private updateMenuSectionItem!: (payload: object) => Promise<void>;
	@Action('createMenuSectionItem', { namespace }) private createMenuSectionItem!: (payload: object) => Promise<void>;
	@Action('uploadItemImage', { namespace }) private uploadItemImageAction!: (payload: object) => Promise<void>;
	@Getter('getSelectedItem', { namespace }) private selectedItem!: MenuItem;

	// Tags
	@Action('fetchTags', { namespace: 'tags' }) private fetchTags!: () => Promise<void>;
	@Action('fetchMenuItemTags', { namespace: 'tags' }) private fetchMenuItemTags!: (args: { menuId: number; itemId: number }) => Promise<MenuItemTag[]>;
	@Action('fetchSectionItemTags', { namespace: 'tags' }) private fetchSectionItemTags!: (args: { menuId: number; sectionId: number; itemId: number }) => Promise<MenuItemTag[]>;
	@Action('createItemTagAssociations', { namespace: 'tags' }) private createItemTagAssociations!: (args: object) => Promise<void>;
	@Action('deleteItemTagAssociations', { namespace: 'tags' }) private deleteItemTagAssociations!: (args: object) => Promise<void>;
	@Getter('getAllTags', { namespace: 'tags' }) private allTags!: MenuItemTag[];

	// Auth
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('isPosIntegrated', { namespace: 'auth' }) private isPosIntegrated!: boolean;
	@Getter('isMarketplaceEnabled', { namespace: 'auth' }) private isMarketplaceEnabled!: boolean;
	@Getter('showReportingCategory', { namespace: 'auth' }) private showReportingCategory!: boolean;
	@Getter('hasSubsidies', { namespace: 'auth' }) private hasSubsidies!: boolean;
	@Getter('isK12Location', { namespace: 'auth' }) private isK12Location!: boolean;
	@Getter('isCateringRestaurant', { namespace: 'auth' }) private isCateringRestaurant!: boolean;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('getPriceAffixes', { namespace: 'auth' }) private priceAffixes!: PriceAffixes;

	@Prop({ type: Boolean, default: true }) private isCreating!: boolean;
	@Prop({ type: Object, default: () => {} }) private item!: MenuItem;
	@Prop({ type: Object, default: () => {} }) private menu!: Menu;
	@Prop({ type: Object, default: () => {} }) private section!: MenuSection;
	@Prop({ type: Boolean, default: false }) private disabled!: boolean;
	@Prop({ type: Object, default: () => {} }) private selectedLocale!: RestaurantLocale;

	@Watch('mutableItem.sold_out')
	onSoldOutChange() {
		if (!this.mutableItem.sold_out) {
			this.mutableItem.auto_restock = 'disabled';
		}
	}

	@Watch('mutableItem.price_on_request')
	onPriceOnRequestChange(isItemPriceOnRequest: boolean): void {
		if (isItemPriceOnRequest) {
			this.mutableItem.price = '0';
			this.mutableItem.base_price = null;
			this.mutableItem.calories = null;
			this.mutableItem.additional_prices = null;
			this.mutableItem.price_schedule = null;
			this.priceAvailability = [];
			this.mutablePricing = this.mutablePricing.map((option: Option) => ({
				...option,
				price: '0.00',
				calories: null
			}));
			this.$emit('set-as-edited');
		}
	}

	$refs!: { observer: Validate; };

	private PERMISSION = PERMISSION;

	// Images
	private S3_BUCKET_BASE_URL: string = process.env.VUE_APP_S3_BUCKET_BASE_URL;
	private mutableImageFile: string | null = null;

	private mutableItem: any = this.item;
	private minQuantityEnabled: boolean = false;

	// Loaders
	private loading: boolean = false;
	private initializationComplete: boolean = false;
	private isLocationInOntario: boolean = false;

	// Valid form
	private formValid: boolean = true;

	// Nutrition
	private mutableIngredients: string[] = this.mutableItem?.ingredients
		? [...this.mutableItem.ingredients.split(',').map((ingredient: string) => ingredient.trim()), '']
		: [''];
	private mutableAllergens: string[] = this.mutableItem?.allergens
		? this.mutableItem.allergens.split(',').map((allergen: string) => allergen.trim())
		: [];
	private mutableDiets: string[] = this.mutableItem?.diets
		? this.mutableItem.diets.split(',').map((diet: string) => diet.trim())
		: [];

	// Pricing
	private itemPricingOptionGroup: OptionGroup|object|undefined = {};
	private mutablePricing: Option[] = [];
	private pricingBatch: Batch = {
		removed: [],
		updated: this.mutablePricing || [],
		added: this.mutablePricing ? [] : [{ ...this.defaultOption }]
	};
	private priceAvailability: ScheduleObject[] = [];
	private isPricingAvailabilityAllWeek: boolean = true;
	private showPriceModifiers: boolean = false;
	private showConfirmDisablePriceModifiers : boolean = false;

	// Addons
	private itemAddOnOptionGroup: OptionGroup[] = [];
	private itemAddOnOptionGroupIds: number[] = [];
	private mutableItemAddOnOptionGroupIds: number[] = [];
	private showAddOnOptionGroupEditor: boolean = false;

	// Tags
	private itemTagNames: string[] = [];
	private mutableTagNames: string[] = [];

	private get isTagLimitReached(): boolean {
		return this.mutableTagNames.length >= 4;
	}

	private get valid(): boolean {
		const { name, price } = this.mutableItem;
		return name && !isFalseyButNotZero(price) && Number(price) >= 0 && (this.showReportingCategory && this.hasPermission(this.PERMISSION.ITEM_ACCOUNTING_CATEGORY) ? !!this.mutableItem.category : true);
	}

	private get defaultOption() {
		return {
			...OPTION,
			price: this.mutableItem.price_on_request ? '0.00' : OPTION.price
		};
	}

	/**
	 * Fetch the option groups
	 *
	 * @return {void}
	 */
	private async created(): Promise<void> {
		const fetchAddOns = (): any => (
			this.isCreating
				? this.fetchAddOnOptionGroups()
				: Promise.all([
					this.fetchOptionGroupsByItem({ menuId: this.menu?.id, sectionId: this.section?.id, itemId: this.item?.id, type: 'pricing' }),
					this.fetchAddOnOptionGroups()
				])
		);

		try {
			// Fetch tags for the selector
			await this.fetchTags();
		}
		catch (errorMessage) {
			Toast.error(this.$t('menu.tags.errors.error_fetching', { errorMessage }));
		}

		// Fetch item's tags fresh from API (similar to fetchOptionGroupsByItem pattern for add-ons)
		if (!this.isCreating && this.item?.id && this.menu?.id) {
			const itemTags = this.section?.id
				? await this.fetchSectionItemTags({ menuId: this.menu.id, sectionId: this.section.id, itemId: this.item.id })
				: await this.fetchMenuItemTags({ menuId: this.menu.id, itemId: this.item.id });

			this.itemTagNames = (itemTags || []).map((tag: MenuItemTag) => tag.name);
		}
		this.mutableTagNames = [...this.itemTagNames];

		await fetchAddOns()
			.catch((errorMessage: any) => {
				Toast.error(this.$t('menu.addon_groups.error_fetching_options', { errorMessage }));
			});

		this.itemAddOnOptionGroup = (this.itemOptionGroups || [])
			.filter((optionGroup: OptionGroup) => optionGroup.type === 'addons');

		this.itemAddOnOptionGroupIds = this.itemAddOnOptionGroup.map((addOnOptionGroup: OptionGroup) => (addOnOptionGroup.id as number));
		this.mutableItemAddOnOptionGroupIds = [...this.itemAddOnOptionGroupIds];

		// Note: each item only has one pricing option group associated to it at a time
		this.itemPricingOptionGroup = (this.itemOptionGroups || [])
			.find((optionGroup: OptionGroup) => optionGroup.type === 'pricing');

		const values: Option[] = (this.itemPricingOptionGroup as OptionGroup)?.values as Option[];
		if (values?.length) {
			this.showPriceModifiers = true;
		}

		this.mutablePricing = values
			? [...JSON.parse(JSON.stringify(values.sort((a: any, b: any) => valueSort(a, b, 'price')))), { ...this.defaultOption }]
			: [{ ...this.defaultOption }];

		if (this.mutableItem.price_schedule?.length) {
			this.setPriceAvailabilityOnLoad();
		}

		if (this.isCreating) {
			this.mutableItem.price_type = this.$t('menu.item.editor.default_price.type_default_value');
			this.mutableItem.tax_rebate_eligibility = 'eligible';
		}

		if (this.mutableItem.min_quantity && this.mutableItem.min_quantity > 1) {
			this.minQuantityEnabled = true;
		}

		this.isLocationInOntario = await this.checkIfLocationInOntario();

		this.initializationComplete = true;
	}

	private destroyed () {
		this.clearItemOptionGroups();
	}

	private formatItemPrice = (price: number) => formatValue(price, '', 'price');

	private formatItemCalories = (calories: number) => formatValue(calories, null, 'calories');

	private formatItemMinQuantity = (minQuantity: number) => formatValue(minQuantity, null, 'quantity', 2);

	private updateDescription(description: string): void {
		this.mutableItem.description = description;
		this.$emit('set-as-edited');
	}

	private updateAlcoholicBeverageCheck(value: boolean): void {
		this.mutableItem.alcoholic_beverage = value;

		if (value && !this.mutableAllergens.includes('Alcohol')) {
			this.mutableAllergens.push('Alcohol');
		}
	}

	private updateNutritionalSheetUrl(url: string): void {
		this.mutableItem.nutritional_sheet_url = url;
	}

	private alcoholUnchecked(chip: string): void {
		if (chip === 'Alcohol' && this.mutableItem.alcoholic_beverage) {
			this.mutableItem.alcoholic_beverage = false;
		}
	}

	private isTagDisabled(tag: MenuItemTag): boolean {
		return this.isTagLimitReached && !this.mutableTagNames.includes(tag.name);
	}

	private onMinQuantityToggleChange(): void {
		if (!this.minQuantityEnabled) {
			this.mutableItem.min_quantity = null;
		}
		else if (this.minQuantityEnabled && !this.mutableItem.min_quantity) {
			this.mutableItem.min_quantity = 2;
		}
	}

	private updateItemTaxRates(taxRates: Taxes | null): void {
		this.mutableItem.tax_rate = taxRates;
	}

	private async closeAddOnOptionGroupEditorModal () {
		this.showAddOnOptionGroupEditor = false;
		this.setSelectedAddOnOptionGroup({});
	}

	private addPricingForm(): void {
		const priceModifiersElement: any = document.getElementById('item-editor-pricing');
		priceModifiersElement.style.height = `${priceModifiersElement.scrollHeight + priceModifiersElement.children[1].scrollHeight}px`;
		this.mutablePricing.push({ ...this.defaultOption });
		this.$emit('set-as-edited');
	}

	private removePricingForm(index: number): void {
		// Remove row
		const existingPricingId: number|undefined = this.mutablePricing[index]?.id;
		if (existingPricingId) {
			this.pricingBatch.removed.push(existingPricingId);
		}
		this.mutablePricing.splice(index, 1);
		this.$emit('set-as-edited');

		// Update height of element
		const priceModifiersElement: any = document.getElementById('item-editor-pricing');
		priceModifiersElement.style.height = `${priceModifiersElement.scrollHeight - priceModifiersElement.children[1].scrollHeight}px`;
	}

	private createItem (item: MenuItem): Promise<void> {
		if (this.section) {
			return this.createMenuSectionItem({ menu: this.menu, section: this.section, item });
		}
		else {
			return this.createMenuItem({ menu: this.menu, item });
		}
	}

	private updateItem (item: MenuItem): Promise<void> {
		if (this.section) {
			return this.updateMenuSectionItem({ menu: this.menu, section: this.section, item });
		}
		else {
			return this.updateMenuItem({ menu: this.menu, item });
		}
	}

	private onChangeAddOnsCheckbox (values: number[]) {
		this.mutableItemAddOnOptionGroupIds = values;
		this.$emit('set-as-edited');
	}

	private setAdditionalPrices(childAdditionalPrices: IObjectKeys): void {
		this.mutableItem.additional_prices = Object.assign({}, childAdditionalPrices);
	}

	private setPriceAvailability(childPriceAvailability: ScheduleObject[]): void {
		this.priceAvailability = childPriceAvailability;
	}

	private handleAutoRestockChange(value: string) {
		this.mutableItem.auto_restock = value;
	}

	private handleTaxRebateEligibilityChange(value: string) {
		this.mutableItem.tax_rebate_eligibility = value;
	}

	/**
	 * Set up the price availability array on load to the format needed
	 * for the UX
	 *
	 * @return {ScheduleObject[]}
	 */
	private setPriceAvailabilityOnLoad(): void {
		for (let index = 1; index < 8; index++) {

			// Set the temp object that will be push in the availability array after
			// and get the schedule (we separate the array anytime a character changes)
			const tempObject: ScheduleObject | any = { day: index, values: [] };
			const schedule: string[] = this.mutableItem.price_schedule[index - 1].match(/(.)\1*/g);

			// If the value contains only A for a day, we still need to pass an empty value for the UI
			if (schedule.length === 1 && new RegExp('^[A\A]+$').test(schedule[0])) {
				// Once the values are pushed to the object, we push it into the price availability array
				tempObject.values.push({ start: '', end: '', priceChosen: '' });
				this.priceAvailability.push(tempObject);
			}
			else {
				let dayTime = 0;

				// Loop through the schedule
				for (let index = 0; index < schedule.length; index++) {
					const scheduleElement: string = schedule[index];
					const numberOfHours: number = scheduleElement.length / 4;
					const tempPriceChosen: string = scheduleElement.charAt(0);
					let tempValue: ScheduleObjectTime | any = { priceChosen: tempPriceChosen };

					// We skip the iteration if the character is A since it means it's a default price
					if (tempPriceChosen === 'A') {
						dayTime += numberOfHours;
						continue;
					}

					// Set the start/end time of the scheduled price
					tempValue.start = dayTime < 10 ? `0${dayTime}:00` : `${dayTime}:00`;
					dayTime += numberOfHours;
					tempValue.end = dayTime < 10 ? `0${dayTime}:00` : `${dayTime}:00`;

					// Add the price/unavailable string for the selector (this is for UX)
					tempValue.priceChosen = `${tempPriceChosen} ${tempPriceChosen === 'X' ? '(' + this.$t('menu.item.editor.price_scheduling.unavailable_option') : '(' + this.$n(Number(this.mutableItem.additional_prices[tempPriceChosen]), 'currency')})`;
					tempObject.values.push(tempValue);
				}

				// Once the values are pushed to the object, we push it into the price availability array
				this.priceAvailability.push(tempObject);
			}
		}

		// Check if it's all week or not
		if (this.mutableItem.price_schedule && !this.mutableItem.price_schedule.every((element: string) => element === this.mutableItem.price_schedule[0])) {
			this.isPricingAvailabilityAllWeek = false;
		}
	}

	/**
	 * Update pricing option group's options
	 *
	 * @param {Option[]} completedPricing
	 * @return {Promise<void>}
	 */
	private createPricingOptionsGroup (completedPricingOptions: Option[]) {
		if (!this.hasPermission(PERMISSION.ITEM_PRICE_OPTIONS)) {
			return;
		}

		this.createPricingOptionGroupAction({
			options: completedPricingOptions,
			body: formatOptionGroup({
				// Note the pricing addons name is currently hard coded to pricing!
				name: 'Pricing',
				type: 'pricing',
				allow_multiple_selection: false,
				required: false
			}),
			associationParams: {
				menuId: this.menu.id,
				sectionId: this.section?.id,
				itemId: this.selectedItem.id
			}
		});
	}

	/**
	 * Update pricing option group's options
	 *
	 * @param {Option[]} completedPricing
	 * @return {Promise<void>}
	 */
	private updatePricingOptions(completedPricingOptions: Option[]): Promise<void> {
		// If there are newly added pricing options or removed ones, skip this
		if (!this.hasPermission(PERMISSION.ITEM_PRICE_OPTIONS) || (!completedPricingOptions.length && !this.pricingBatch.removed.length)) {
			return Promise.resolve();
		}
		// Remove the pricing options that do not have an id, those are to be *added*, as opposed to *updated*
		this.pricingBatch.updated = completedPricingOptions.filter((updatedPricing: Option) => updatedPricing.id);

		// Check which pricing options were actually updated. The ones that are unchanged will be removed.
		if (this.pricingBatch.updated.length) {
			const existingPricingOptionsById: any = ((this.itemPricingOptionGroup as OptionGroup)?.values as Option[]).reduce((pricingOptionByIdObj: any, current: Option) => {
				return {
					...pricingOptionByIdObj,
					[current.id!]: current
				};
			}, {});
			this.pricingBatch.updated = this.pricingBatch.updated.filter((pricingOption: Option) => {
				return (
					pricingOption.price !== existingPricingOptionsById[pricingOption.id!].price ||
					pricingOption.name !== existingPricingOptionsById[pricingOption.id!].name ||
					pricingOption.calories !== existingPricingOptionsById[pricingOption.id!].calories ||
					pricingOption.localization !== existingPricingOptionsById[pricingOption.id!].localization
				);
			});
		}

		// The ones that don't have an id are to be added:
		this.pricingBatch.added = completedPricingOptions.filter(pricingOption => !pricingOption.id);

		return this.updateOptionGroupAndOptions({
			id: (this.itemPricingOptionGroup as OptionGroup)?.id,
			optionsBatch: this.pricingBatch
		});
	}

	private uploadItemImage (item: MenuItem): any {
		if (this.mutableImageFile) {
			return this.uploadItemImageAction({
				menu: this.menu,
				section: this.section,
				item,
				image: this.mutableImageFile
			});
		}
		return;
	}

	/**
	 * Depending on the additional prices object and the price
	 * availability array, we set them back to their default values (NULL)
	 * or we run some logic to set the price schedule.
	 *
	 * @return {void}
	 */
	private setItemAvailabilityConfig(): void {
		if (this.priceAvailability && this.priceAvailability.length) {
			Vue.set(this.mutableItem, 'price_schedule', []);

			for (let index = 0; index < this.priceAvailability.length; index++) {
				const tempPriceAvailability = this.priceAvailability[index];

				// Day is 0 if the user sets if for all week
				this.setItemDayAvailability(tempPriceAvailability);
			}

			// Check if the array contains only the A characters which mean it's only the default price scheduled,
			// we can empty that array.
			if (this.mutableItem.price_schedule.every((element: string) => new RegExp('^[A\A]+$').test(element))) {
				this.mutableItem.price_schedule = null;
			}
		}
		else {
			this.mutableItem.additional_prices = null;
			this.mutableItem.price_schedule = null;
		}
	}

	/**
	 * Set the specific item's day availability passed
	 *
	 * @param {ScheduleObject} priceAvailability
	 * @return {string}
	 */
	private setItemDayAvailability(tempPriceAvailability: ScheduleObject): void {
		let availability = '';
		let baseTime = 0;

		// Since it's all week, we just push the availability for the 7 full days
		// Loop through all 24 hours for the availability
		do {
			let availabilityCollided: boolean = false;

			for (let valuesIndex = 0; valuesIndex < tempPriceAvailability.values.length; valuesIndex++) {
				const value: ScheduleObjectTime = tempPriceAvailability.values[valuesIndex];
				const startTime: number = DateTime.fromFormat(value.start, 'HH:mm').hour;
				const endTime: number = value.end === '24:00' ? 24 : DateTime.fromFormat(value.end, 'HH:mm').hour;

				// Specific time chosen with price id found
				if (baseTime === startTime) {

					// Check amount of time the price will be different
					const startEndDifference = endTime - startTime;

					// Make sure the availability chosen is in positive time
					if (startEndDifference > 0) {
						// Parse the price chosen to have only the key
						const priceChosenKey: string = value.priceChosen.charAt(0);
						// Update the availability with the price id chosen, we repeat by 4 for the full hour since
						// it's in 15mins increment on the backend, and we repeat the number of hours between start and end time.
						availability += (priceChosenKey.repeat(4)).repeat(startEndDifference);
						baseTime += startEndDifference;
						availabilityCollided = true;
					}
				}
			}

			// If the availability have not collided we simply add the default price for the hour
			if (!availabilityCollided) {
				availability += 'AAAA';
				baseTime++;
			}
		} while (baseTime < 24);
		this.mutableItem.price_schedule.push(availability);
	}

	private async save () {
		const isValid = await this.$refs.observer.validate();
		if (!isValid) {
			Toast.error(this.$t('shared.error_missing_required_fields'));
			scrollToFirstError();
			return;
		}

		this.loading = true;

		const completedPricingOptions: Option[] = this.mutablePricing.filter((pricingOption: Option) => pricingOption.price && pricingOption.name);
		this.setItemAvailabilityConfig();

		// Update the item itself
		const item = formatItem({
			...this.mutableItem,
			ingredients: this.mutableIngredients.filter(Boolean).map((ingredient: string) => ingredient.trim()).join(),
			allergens: this.mutableAllergens.join(),
			diets: this.mutableDiets.join()
		});

		if (this.isCreating) {
			await this.createItem(item)
				.then(() => this.uploadItemImage(this.selectedItem))
				.then(() => this.createPricingOptionsGroup(completedPricingOptions))
				.then(() => {
					const { id: createdItemId } = this.selectedItem;

					return Promise.all([
						this.createItemOptionGroupAssociation({
							menuId: this.menu.id,
							sectionId: this.section?.id,
							itemId: createdItemId,
							optionGroupIdsArray: this.mutableItemAddOnOptionGroupIds.filter((id) => !this.itemAddOnOptionGroupIds.includes(id))
						}),
						this.createItemTagAssociations({
							menuId: this.menu.id,
							sectionId: this.section?.id,
							itemId: createdItemId,
							tagNamesArray: this.mutableTagNames
						})
					]);
				})
				.then(() => {
					this.$emit('close-modal', true);
				})
				.catch((errorMessage: string) => {
					Toast.error(this.$t('menu.items.error_creating_item', { itemName: this.mutableItem.name, errorMessage }));
				});
		}
		else {
			await this.updateItem(item)
				.then(() => this.uploadItemImage(item))
				.then(() => (this.itemPricingOptionGroup as OptionGroup)?.id ? this.updatePricingOptions(completedPricingOptions) : this.createPricingOptionsGroup(completedPricingOptions))
				.then(() => {
					return Promise.all([
						this.createItemOptionGroupAssociation({
							menuId: this.menu.id,
							sectionId: this.section?.id,
							itemId: item.id,
							optionGroupIdsArray: this.mutableItemAddOnOptionGroupIds.filter((id) => !this.itemAddOnOptionGroupIds.includes(id))
						}),
						this.deleteItemOptionGroupAssociation({
							menuId: this.menu.id,
							sectionId: this.section?.id,
							itemId: item.id,
							optionGroupIdsArray: this.itemAddOnOptionGroupIds.filter((id) => !this.mutableItemAddOnOptionGroupIds.includes(id))
						}),
						this.createItemTagAssociations({
							menuId: this.menu.id,
							sectionId: this.section?.id,
							itemId: item.id,
							tagNamesArray: this.mutableTagNames.filter((name) => !this.itemTagNames.includes(name))
						}),
						this.deleteItemTagAssociations({
							menuId: this.menu.id,
							sectionId: this.section?.id,
							itemId: item.id,
							tagNamesArray: this.itemTagNames.filter((name) => !this.mutableTagNames.includes(name))
						})
					]);
				})
				.then(() => {
					this.$emit('close-modal', true);
				})
				.catch((errorMessage: string) => {
					Toast.error(this.$t('menu.items.error_updating_item', { itemName: this.mutableItem.name, errorMessage }));
				});
		}
		this.loading = false;
	}

	/**
	 * Check if needs to show the confirm disable price modifiers modal to the user
	 *
	 * @return {void}
	 */
	private confirmDisablePriceModifiers(): void {
		if (this.showPriceModifiers) {
			isEqual(this.mutablePricing, [{ ...this.defaultOption }]) ? this.showPriceModifiers = false : this.showConfirmDisablePriceModifiers = true;
		}
		else {
			this.showPriceModifiers = true;
		}
	}

	/**
	 * Remove price modifiers and hide/disable the section
	 *
	 * @return {void}
	 */
	private disablePriceModifiers(): void {
		this.showConfirmDisablePriceModifiers = false;
		this.showPriceModifiers = false;

		const pricingIds = this.mutablePricing.map(pricing => pricing.id).filter(pricing => pricing !== undefined);
		pricingIds.forEach(pricingId => {
			this.pricingBatch.removed.push(pricingId as number);
		});
		this.mutablePricing = [{ ...this.defaultOption }];
		this.$emit('set-as-edited');
	}

	/**
	 * Update price name to 'Regular' if empty on blur, remove it on focus
	 * @param {string} eventType
	 * @return {void}
	 */
	private checkPriceType(eventType: string): void {
		if (eventType === 'blur' && this.mutableItem.price_type === '') {
			this.mutableItem = { ...this.mutableItem, price_type: this.$t('menu.item.editor.default_price.type_default_value') };
		}
		else if (eventType === 'focus' && this.mutableItem.price_type === this.$t('menu.item.editor.default_price.type_default_value')) {
			this.mutableItem = { ...this.mutableItem, price_type: '' };
		}
	}

	/**
	 * Check if the location is in Ontario
	 *
	 * @return {Promise<boolean>}
	 */
	private async checkIfLocationInOntario(): Promise<boolean> {
		if(this.restaurant.address) {
			try {
				const response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(this.restaurant.address)}&key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}`);
				const results = response.data.results;

				if (results.length) {
					const location = results[0].address_components;
					const province = location.find((component: any) => component.types.includes('administrative_area_level_1'));
					const country = location.find((component: any) => component.types.includes('country'));

					if (province && country) {
						return province.long_name === 'Ontario' && country.short_name === 'CA';
					}
				}

				return false;
			}
			catch (error) {
				return false;
			}
		}
		return false;
	}
}
